import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { api } from '@/utils/api';
import SectionTitle from '@/components/ui/SectionTitle';
import Pagination from '@/components/ui/Pagination';
import AtualizadoObraCard, { type AtualizadoObraCardObra } from '@/components/obra/AtualizadoObraCard';
import { OBRAS_GENEROS, HOME_FORMATO_PADRAO, accentHexForFormato } from '@/constants/obras-generos';

const PAGE_LIMIT = 18;

function normalizeAtualizadaItem(input: AtualizadoObraCardObra & Record<string, unknown>): AtualizadoObraCardObra {
    const ultimos = Array.isArray(input.ultimos_capitulos)
        ? (input.ultimos_capitulos as Array<Record<string, unknown>>).map((c) => ({
              id: Number(c.cap_id ?? 0) || undefined,
              numero: typeof c.cap_num === 'number' ? c.cap_num : undefined,
              nome: typeof c.cap_nome === 'string' ? c.cap_nome : undefined,
              cap_lancado_em: c.publicado_em,
          }))
        : [];
    const totalCap =
        typeof input.total_capitulos === 'number'
            ? input.total_capitulos
            : typeof input.total_capitulos_ativos === 'number'
              ? input.total_capitulos_ativos
              : undefined;
    return {
        ...input,
        capitulos: input.capitulos ?? ultimos,
        ...(totalCap !== undefined ? { total_capitulos: totalCap } : {}),
    };
}

const AtualizadosGridSkeleton = () => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 md:grid-cols-5 lg:grid-cols-6">
        {[...Array(10)].map((_, i) => (
            <div key={i} className="flex min-w-0 flex-col gap-2 p-0.5">
                <div className="aspect-[2/3] w-full animate-pulse rounded-2xl bg-white/[0.08]" />
                <div className="h-4 w-full animate-pulse rounded bg-white/[0.06]" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-white/[0.06]" />
                <div className="h-10 w-full animate-pulse rounded-lg bg-white/[0.06]" />
                <div className="h-10 w-full animate-pulse rounded-lg bg-white/[0.06]" />
            </div>
        ))}
    </div>
);

export default function AtualizadosPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const paginaParsed = parseInt(searchParams.get('pagina') || '1', 10);
    const pagina = Number.isFinite(paginaParsed) && paginaParsed > 0 ? paginaParsed : 1;
    const formatoRaw = searchParams.get('formato');
    const formatoParsed = formatoRaw != null && formatoRaw !== '' ? parseInt(formatoRaw, 10) : 0;
    const formatoId = Number.isFinite(formatoParsed) && formatoParsed > 0 ? formatoParsed : 0;

    const knownFormato = formatoId > 0 && OBRAS_GENEROS.some((g) => g.formato === formatoId);
    const effectiveFormato = knownFormato ? formatoId : HOME_FORMATO_PADRAO;

    const [obras, setObras] = useState<AtualizadoObraCardObra[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    const accent = accentHexForFormato(effectiveFormato);

    useEffect(() => {
        if (knownFormato) return;
        const next = new URLSearchParams(searchParams.toString());
        next.set('formato', String(HOME_FORMATO_PADRAO));
        if (!next.get('pagina')) next.set('pagina', '1');
        setSearchParams(next, { replace: true });
    }, [knownFormato, searchParams, setSearchParams]);

    const handleSelectFormato = (id: number) => {
        const next = new URLSearchParams(searchParams);
        next.set('formato', String(id));
        next.set('pagina', '1');
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        const fetchObras = async () => {
            setLoading(true);
            try {
                const limit = PAGE_LIMIT;
                const offset = (pagina - 1) * limit;
                const { data } = await api.get('obras/atualizadas-recentes', {
                    params: {
                        limit,
                        offset,
                        formato: effectiveFormato,
                    },
                });

                const raw = data?.items ?? (Array.isArray(data) ? data : []);
                const items = Array.isArray(raw)
                    ? raw.filter((o): o is Record<string, unknown> => o != null && typeof o === 'object' && 'id' in o)
                    : [];
                const list = items.map((o) =>
                    normalizeAtualizadaItem({
                        ...(o as AtualizadoObraCardObra),
                        id: Number((o as { id?: unknown }).id),
                    } as AtualizadoObraCardObra & Record<string, unknown>),
                );

                setObras(list);

                const total = data?.total ?? list.length;
                setTotalPages(Math.max(1, Math.ceil(total / limit)));
            } catch (err) {
                console.error('Erro ao buscar atualizados:', err);
                setObras([]);
            } finally {
                setLoading(false);
            }
        };
        void fetchObras();
    }, [pagina, effectiveFormato]);

    return (
        <main className="min-h-screen bg-black pb-24 pt-20 md:pb-20 md:pt-24">
            <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-8">
                <header className="mb-8">
                    <SectionTitle icon={Clock} accentColor={accent}>
                        Recentemente Atualizados
                    </SectionTitle>

                    <nav className="flex flex-wrap gap-2 sm:gap-2.5" aria-label="Filtrar por formato">
                        {OBRAS_GENEROS.map((genero) => {
                            const ativo = genero.formato === effectiveFormato;
                            return (
                                <button
                                    key={genero.id}
                                    type="button"
                                    onClick={() => handleSelectFormato(genero.formato)}
                                    className={`rounded-full border px-3.5 py-2 text-xs font-medium whitespace-nowrap transition-colors sm:px-4 sm:text-sm ${
                                        ativo
                                            ? 'ring-1 ring-inset'
                                            : 'border-white/10 bg-white/[0.04] text-white/75 hover:border-white/15 hover:bg-white/[0.08] hover:text-white'
                                    }`}
                                    style={
                                        ativo
                                            ? {
                                                  backgroundColor: `${genero.color}24`,
                                                  color: genero.color,
                                                  borderColor: genero.color,
                                              }
                                            : undefined
                                    }
                                >
                                    {genero.label}
                                </button>
                            );
                        })}
                    </nav>
                </header>

                {loading ? (
                    <AtualizadosGridSkeleton />
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 md:grid-cols-5 lg:grid-cols-6">
                            {obras.map((obra) => (
                                <div key={obra.id} className="min-w-0 overflow-visible p-0.5">
                                    <AtualizadoObraCard obra={obra} formatoId={effectiveFormato} />
                                </div>
                            ))}
                        </div>

                        {obras.length === 0 && (
                            <div className="py-20 text-center text-white/45">
                                Nenhuma obra encontrada.
                            </div>
                        )}

                        <Pagination
                            currentPage={pagina}
                            totalPages={totalPages}
                            baseUrl="/atualizados"
                            extraParams={{ formato: effectiveFormato }}
                        />
                    </>
                )}
            </div>
        </main>
    );
}
